import { useRef, useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { branded, useBrand } from '@/brand/context'
import { formatDate } from '@/lib/format'
import { idempotencyKey } from '@/lib/idempotency'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { MarkdownEditor } from './MarkdownEditor'

type Approval = components['schemas']['ApprovalOut']
type ApprovalDetail = components['schemas']['ApprovalDetailOut']
type ApprovalField = components['schemas']['ApprovalFieldOut']
type ApprovalLine = components['schemas']['ApprovalLineOut']
type Rationale = components['schemas']['ApprovalRationaleOut']
type ContentDetail = components['schemas']['ContentDetailOut']
type QualityCheck = components['schemas']['QualityCheckOut']

/**
 * Approvals — S-D5's `seo#approve` and `seo#gating`, on real items (S-B27).
 *
 * **The most important screen in the product**, in the design's own words, and
 * the three things its note asks for are what the layout is built around: you can
 * see exactly what changes, you can see *why* without leaving, and you can edit
 * before accepting. So the diff, the reasoning and the editor are one view — the
 * reasoning is a panel beside the change rather than a link, because an approval
 * made without it is a rubber stamp.
 *
 * **The gating is its own screen at `/seo/approvals/gating`** (parity
 * P-GATE-01; it had been a second tab here). *What we can do without asking* is
 * the same decision as the queue, made in advance and for every future run, so
 * the way to it is a button on this header, in the queue's own words — never a
 * `/settings` entry the person doing approvals is nowhere near.
 *
 * **The queue is organisation-wide and the gating is per site**, which is the
 * API's shape and `app/v1/seo_approvals.py` argues both halves: one morning pass
 * over everything waiting, and a separate answer to *"may we publish without
 * asking"* for a staging domain and a client's live one.
 *
 * WHAT THE BUTTON SAYS
 * ----------------------
 * S-D5 draws *"Approve & publish"*, and since 2026-09-19 that is what it says
 * **when the site has a publish target**: `ApprovalOut.publish_target` names
 * it, and the decision itself queues the publish in the same transaction
 * (`app/seo/approvals._act_on_approval`). Before that the button said *Approve*
 * because approving moved the document to `approved` and no further — the
 * tenant's own words were *"I approved it and nothing happened"* — and a
 * screen that claims an act it did not perform is the one kind of wrong an
 * approval queue must never be. The same rule now cuts the other way: a site
 * with no target still gets *Approve*, and the caption says where to choose one.
 */
export function ApprovalQueue(): JSX.Element {
  const [trail, setTrail] = useState<readonly string[]>([])

  const cursor = trail[trail.length - 1] ?? null

  const queue = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/approvals', {
          params: { query: cursor === null ? {} : { cursor } },
          signal,
        }),
      ),
    [cursor],
  )

  const waiting = queue.state.data?.waiting ?? 0

  return (
    <>
      <div className="ar-row">
        {/* SEO-UI-84. *Approval queue*, which is S-D5's heading and the name the
            product uses for this screen everywhere else — the empty state below
            says *"lands here first"* about a queue, `Change`'s note calls it one,
            and `ContentPipeline` links to *the approval queue*. The sidebar's
            *Approvals* is a nav label, where a two-word entry is right; a page
            heading has room to say what the page is. */}
        <h1 className="ar-h1">Approval queue</h1>
        {waiting > 0 ? (
          <span className="ar-badge ar-badge--warning">{`${String(waiting)} waiting`}</span>
        ) : null}
        <span className="app-spacer" />
        {/* The gating screen is its own page (parity P-GATE-01); this is the way
            to it from the queue, in the queue's own words. */}
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/settings/approvals">
          What we can do without asking
        </Link>
      </div>

      <QueueTab
        queue={queue.state}
        refetch={queue.refetch}
        canPageBack={trail.length > 0}
        onOlder={(next) => {
          setTrail([...trail, next])
        }}
        onNewer={() => {
          setTrail(trail.slice(0, -1))
        }}
      />
    </>
  )
}

/* --- the queue ---------------------------------------------------------------- */

type QueueState = ReturnType<typeof useQuery<components['schemas']['ApprovalQueueOut']>>['state']

/**
 * The list, the change, and the decision.
 *
 * Its own component so that the selection, the draft and the reject dialog reset
 * when the tab is left — a half-typed rejection reason that survived a trip
 * through the gating screen and attached itself to a different article is the
 * bug this shape makes unreachable.
 */
function QueueTab({
  queue,
  refetch,
  canPageBack,
  onOlder,
  onNewer,
}: {
  queue: QueueState
  refetch: () => void
  canPageBack: boolean
  onOlder: (cursor: string) => void
  onNewer: () => void
}): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [checked, setChecked] = useState<readonly string[]>([])
  const [rejecting, setRejecting] = useState<Approval | null>(null)
  const [reason, setReason] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)

  const items = queue.data?.items ?? []
  const next = queue.data?.next_cursor ?? null
  // The first item, until one is chosen — and back to the first when the chosen
  // one leaves the queue, which is what a decision does to it.
  const selected = items.some((item) => item.id === selectedId)
    ? selectedId
    : (items[0]?.id ?? null)

  const detail = useQuery(
    (signal) =>
      selected === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/approvals/{approval_id}', {
              params: { path: { approval_id: selected } },
              signal,
            }),
          ),
    [selected],
  )

  const decide = useMutation((input: { id: string; approve: boolean; reason: string | null }) =>
    call(() =>
      api.POST('/api/v1/seo/approvals/{approval_id}/decision', {
        params: {
          path: { approval_id: input.id },
          header: {
            'Idempotency-Key': idempotencyKey(input.approve ? 'approve' : 'reject', input.id),
          },
        },
        body: { approve: input.approve, reason: input.reason },
      }),
    ),
  )

  const bulk = useMutation((ids: readonly string[]) =>
    call(() =>
      api.POST('/api/v1/seo/approvals/decisions', {
        params: {
          // The ids are not in the key: a hundred UUIDs is past the 200
          // characters `app/idempotency.validate` allows, and the random half is
          // what makes a key unique in any case.
          header: { 'Idempotency-Key': idempotencyKey('approve-many', String(ids.length)) },
        },
        body: { approval_ids: [...ids], approve: true, reason: null },
      }),
    ),
  )

  const saveEdits = useMutation((input: Draft) =>
    call(() =>
      api.PUT('/api/v1/seo/approvals/{approval_id}/edits', {
        params: {
          path: { approval_id: input.id },
          header: { 'Idempotency-Key': idempotencyKey('edits', input.id) },
        },
        body: {
          title: input.title,
          meta_description: input.meta,
          body_markdown: input.body,
          image_alt: input.alts,
        },
      }),
    ),
  )

  const loaded = detail.state.data ?? null
  const stored = loaded === null ? null : draftOf(loaded)
  // Held against the id it was typed for, so switching items cannot carry one
  // article's words onto another's. No effect and no reset: the draft is simply
  // ignored the moment it stops matching what is on screen.
  const editing = stored === null ? null : draft !== null && draft.id === stored.id ? draft : stored
  const dirty = stored !== null && editing !== null && !sameDraft(stored, editing)

  // S-G6, audit §G13 and §V2. The gate's verdict on the article being approved,
  // and the run its reasoning belongs to. A second request per selected item
  // rather than a field on `ApprovalDetailOut`: an approval is not always about
  // a document — a `technical_fix` has no gate and no draft — and widening the
  // approval schema to carry a content-only object would put a null on every
  // row of the other kind.
  const article = useQuery(
    (signal) =>
      loaded === null || loaded.subject_type !== 'content'
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/content/{content_id}', {
              params: { path: { content_id: loaded.subject_id } },
              signal,
            }),
          ),
    [loaded?.subject_type, loaded?.subject_id],
  )

  // **The answer in hand, only while it is about the item on screen.** Moving to
  // the next approval changes `loaded` a render before this query is re-issued,
  // and rendering the previous article's gate under the new one's heading is the
  // one thing an approval screen must never do — somebody approves against a
  // verdict that belongs to a different document. Null in that render, which the
  // card below shows as loading.
  const gated =
    article.state.data !== null && article.state.data.id === loaded?.subject_id
      ? article.state.data
      : null

  function afterDecision(): void {
    setDraft(null)
    setSelectedId(null)
    setChecked([])
    refetch()
  }

  return (
    <div>
      {decide.state.status === 'error' && rejecting === null ? (
        <InlineError failure={decide.state.failure} />
      ) : null}
      {bulk.state.status === 'error' ? <InlineError failure={bulk.state.failure} /> : null}

      {queue.status === 'loading' ? (
        <Loading label="Loading the approval queue" />
      ) : queue.status === 'error' ? (
        <ErrorState failure={queue.failure} onRetry={refetch} />
      ) : items.length === 0 ? (
        <Empty
          icon="✓"
          title="Nothing is waiting"
          body={
            `When ${brand.productName} writes a post, rewrites a title or proposes a technical fix, it lands ` +
            'here first. Nothing reaches your site until somebody approves it.'
          }
          action={<Link to="/seo/audit">See what {brand.productName} has found</Link>}
        />
      ) : (
        <div className="app-approve">
          <div className="ar-stack">
            {detail.state.status === 'loading' ? (
              <Loading label="Loading the change" lines={6} />
            ) : detail.state.status === 'error' ? (
              <ErrorState failure={detail.state.failure} onRetry={detail.refetch} />
            ) : loaded === null || editing === null ? null : (
              <Change
                detail={loaded}
                draft={editing}
                dirty={dirty}
                position={items.findIndex((item) => item.id === loaded.id) + 1}
                total={items.length}
                deciding={decide.pending}
                saving={saveEdits.pending}
                saveFailure={
                  saveEdits.state.status === 'error' ? saveEdits.state.failure.message : undefined
                }
                onEdit={setDraft}
                onSave={() => {
                  void saveEdits.run(editing).then((result) => {
                    if (!result.ok) return
                    setDraft(null)
                    detail.refetch()
                    toast.show({
                      kind: 'success',
                      title: 'Your edits are saved',
                      body: `What publishes is what you just wrote, not what ${brand.productName} drafted.`,
                    })
                  })
                }}
                onApprove={() => {
                  // **Unsaved edits are saved first, and this was a real defect.**
                  // *Approve* used to decide straight away, so a tenant who
                  // rewrote a paragraph and pressed the primary button — rather
                  // than the quiet *Save my edits* beside it — approved the text
                  // AugRank had drafted, and the toast told them it was theirs.
                  // The whole exit criterion of this session is that what
                  // publishes is what they wrote, and the screen was the one
                  // place it was not true.
                  const id = loaded.id
                  const decided = (): Promise<void> =>
                    decide.run({ id, approve: true, reason: null }).then((result) => {
                      if (!result.ok) return
                      afterDecision()
                      const target = result.data.publishes_to ?? null
                      toast.show({
                        kind: 'success',
                        title: target
                          ? `Approved — publishing to ${targetName(target)}`
                          : 'Approved',
                        body: target
                          ? 'Recorded against your name. It goes out with the exact text you approved; Where we publish shows it land.'
                          : 'Recorded against your name, with the exact text you approved.',
                      })
                    })
                  // `editing` is non-null in this branch — the render above
                  // returns early otherwise — so `dirty` is the whole question.
                  if (!dirty) {
                    void decided()
                    return
                  }
                  void saveEdits.run(editing).then((saved) => {
                    if (!saved.ok) return undefined
                    setDraft(null)
                    return decided()
                  })
                }}
                onReject={() => {
                  decide.reset()
                  setReason('')
                  setRejecting(loaded)
                }}
                onImageChanged={() => {
                  // The approval reads `content.meta` live, so a picture that changed
                  // under it has to be re-read or the screen keeps rendering the old
                  // one at an index that may no longer exist.
                  detail.refetch()
                  // **And the draft's alt text has to let go with it.** It is
                  // positional against a list that just changed length, so holding it
                  // would put the dropped picture's sentence under the surviving one.
                  // Only the alts: the title, the meta and the body are about words
                  // rather than pictures, and throwing away unsaved prose because
                  // somebody changed an image would be the larger loss.
                  setDraft(draft === null ? null : { ...draft, alts: [] })
                }}
              />
            )}
          </div>

          <div className="ar-stack">
            <Why detail={loaded} article={gated} />
            {loaded === null || loaded.subject_type !== 'content' ? null : (
              <QualityGate article={gated} failure={article.state.failure} />
            )}
            <WaitingList
              items={items}
              selected={selected}
              checked={checked}
              busy={bulk.pending}
              nextCursor={next}
              canPageBack={canPageBack}
              onSelect={(id) => {
                setSelectedId(id)
              }}
              onCheck={(id, on) => {
                setChecked(on ? [...checked, id] : checked.filter((each) => each !== id))
              }}
              onCheckLowRisk={(ids) => {
                // Added to what is already ticked rather than replacing it: a
                // tenant who read and ticked one new post, then reached for this,
                // meant *and these too*. `Set` because the two lists overlap when
                // the press is repeated.
                setChecked([...new Set([...checked, ...ids])])
              }}
              onApproveSelected={() => {
                void bulk.run(checked).then((result) => {
                  if (!result.ok) return
                  afterDecision()
                  const outcomes = result.data.outcomes ?? []
                  const asked = outcomes.length
                  const missed = asked - result.data.decided
                  const publishing = outcomes.filter((item) => item.publishes_to).length
                  const recorded =
                    missed === 0
                      ? 'Each one is recorded separately, so the log can still say who approved what.'
                      : `${String(missed)} had already been decided by somebody else and were left alone.`
                  toast.show({
                    kind: 'success',
                    title: `Approved ${String(result.data.decided)} of ${String(asked)}`,
                    body:
                      publishing > 0
                        ? `${String(publishing)} ${publishing === 1 ? 'is' : 'are'} publishing now. ${recorded}`
                        : recorded,
                  })
                })
              }}
              onOlder={onOlder}
              onNewer={onNewer}
            />
          </div>
        </div>
      )}

      {rejecting === null ? null : (
        <Modal
          title="Reject this change?"
          onClose={() => {
            setRejecting(null)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setRejecting(null)
                }}
              >
                Keep it waiting
              </Button>
              <Button
                tone="danger"
                writes
                busy={decide.pending}
                busyLabel="Rejecting"
                onClick={() => {
                  const target = rejecting
                  const said = reason.trim()
                  void decide
                    .run({ id: target.id, approve: false, reason: said === '' ? null : said })
                    .then((result) => {
                      if (!result.ok) return
                      setRejecting(null)
                      setReason('')
                      afterDecision()
                      toast.show({
                        kind: 'success',
                        title: 'Rejected',
                        body: 'Nothing was published, and your reason is on the record.',
                      })
                    })
                }}
              >
                Reject
              </Button>
            </>
          }
        >
          <InlineError failure={decide.state.failure} />
          <p style={{ marginTop: 0 }}>
            {rejecting.title ?? rejecting.change_label} will not be published, and will not come
            back on the next run unless something about the page changes.
          </p>
          {/* SEO-UI-110. *Reason*, which is S-D5's and is the right word for a
              form label: *Why* is a question, and a label that asks one reads as
              though the field beside it is the answer to something the screen just
              said. The help sentence carries the rest, and *Keep it waiting* stays
              where the drawing has *Cancel* — in a destructive dialog *Cancel* is
              genuinely ambiguous about which of the two things it cancels. */}
          <Field
            label="Reason"
            help={`Optional, and worth writing: it is what stops ${brand.productName} proposing the same thing again, and it is kept with the decision in your audit log.`}
          >
            {(aria) => (
              <textarea
                className="ar-textarea"
                {...aria}
                value={reason}
                maxLength={2000}
                placeholder="The price figures are out of date."
                onChange={(event) => {
                  setReason(event.currentTarget.value)
                }}
              />
            )}
          </Field>
        </Modal>
      )}
    </div>
  )
}

/* --- one change --------------------------------------------------------------- */

/**
 * The card: what changes, the words to change, and the two presses.
 *
 * **The diff is rendered, never recomputed.** It arrives already worked out and
 * `read_approval` says why — it was captured when the item was filed, so what a
 * tenant approves is what they were shown even if the draft has since moved. A
 * second differ in TypeScript would be the copy that disagreed.
 */
function Change({
  detail,
  draft,
  dirty,
  position,
  total,
  deciding,
  saving,
  saveFailure,
  onEdit,
  onSave,
  onApprove,
  onReject,
  onImageChanged,
}: {
  detail: ApprovalDetail
  draft: Draft
  dirty: boolean
  position: number
  total: number
  deciding: boolean
  saving: boolean
  saveFailure?: string | undefined
  onEdit: (draft: Draft) => void
  onSave: () => void
  onApprove: () => void
  onReject: () => void
  onImageChanged: () => void
}): JSX.Element {
  const brand = useBrand()
  const fields = detail.diff.fields ?? []
  const hunks = detail.diff.hunks ?? []
  const actions = detail.diff.actions ?? []
  const images = detail.images ?? []
  const editable = detail.body_markdown !== null && detail.body_markdown !== undefined
  // The image routes live under the *document*, not the approval — `subject_id`
  // is the content id and the queue is deliberately not the only way to reach a
  // draft. Null for a `technical_fix`, which has no document and no images either.
  const contentId = detail.subject_type === 'content' ? detail.subject_id : null
  const publishTarget = detail.publish_target ?? null
  const technical = detail.subject_type === 'technical_fix'
  const results = technical ? (detail.results ?? null) : null

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <div>
          <h2>{detail.title ?? detail.change_label}</h2>
          <div className="ar-mono ar-subtle app-caption">
            {detail.slug === null || detail.slug === undefined
              ? (detail.site_domain ?? 'This organisation')
              : `/${detail.slug}`}
          </div>
        </div>
        <span className="ar-card__spacer" />
        {detail.stale ? <span className="ar-badge ar-badge--warning">Waiting a while</span> : null}
        {/* SEO-UI-63. S-D5 gates the editor behind an *Edit before approving*
            toggle and this screen leaves it open, which is deliberate: the note on
            that screen says three things have to be true at once — you can see what
            changes, see why, and edit before accepting — and a mode switch puts a
            press between reading a sentence and fixing it.

            **What the toggle was really protecting is announced instead of
            prevented.** Its worth is that an approver knows whether they are in a
            state where typing changes what publishes; with an always-open editor
            that state is simply *"you have typed something"*, and this badge says
            so. It is not decoration next to `Edited` — that one is the server's
            record that some earlier session edited this item, and this one is the
            unsaved text in the browser right now. */}
        {dirty ? <span className="ar-badge ar-badge--warning">Unsaved edits</span> : null}
        {detail.edited ? <span className="ar-badge ar-badge--info">Edited</span> : null}
        <span className="ar-badge ar-badge--neutral">{`${String(position)} of ${String(total)}`}</span>
      </div>

      <div className="ar-card__body ar-stack">
        {detail.diff.truncated ? (
          <div className="ar-alert ar-alert--warning">
            <span className="ar-alert__icon" aria-hidden="true">
              !
            </span>
            <div className="ar-alert__body">
              <strong>This change was too large to store as a diff</strong>
              The summary below is partial. The whole text is in the editor, which is what publishes
              — read that before approving.
            </div>
          </div>
        ) : null}

        {fields.map((field) => (
          <FieldDiff key={field.field} field={field} />
        ))}

        {hunks.length === 0 ? null : (
          <div>
            <div className="ar-row app-diff-head">
              <strong className="app-label-sm">
                {detail.diff.is_new ? 'Body — a new page' : 'Body — what changes'}
              </strong>
              <span className="ar-subtle app-caption">
                {`+${String(detail.diff.added)} added · −${String(detail.diff.removed)} removed`}
              </span>
            </div>
            <div className="app-diff">
              {hunks.flatMap((hunk, hunkIndex) =>
                (hunk.lines ?? []).map((line, lineIndex) => (
                  <DiffRow key={`${String(hunkIndex)}-${String(lineIndex)}`} line={line} />
                )),
              )}
            </div>
          </div>
        )}

        {actions.length === 0 ? null : (
          <div>
            <strong className="app-label-sm">
              {results ? 'What was proposed' : 'What would be changed'}
            </strong>
            <ul className="ar-stack ar-stack--tight" style={{ marginTop: 'var(--ar-space-3)' }}>
              {actions.map((action) => (
                <li key={`${action.code}-${action.url}`} className="ar-panel">
                  <strong className="app-label-sm">{action.label}</strong>
                  {action.url === '' ? null : (
                    <span className="ar-mono ar-subtle app-editor__block">{action.url}</span>
                  )}
                  {action.remedy === '' ? null : (
                    <span className="ar-subtle app-editor__block">
                      {branded(action.remedy, brand)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {results ? <FixResults results={results} /> : null}

        {editable ? (
          <>
            <Field
              label="Page title"
              help="What Google shows as the headline. Roughly sixty characters before it is cut off."
            >
              {(aria) => (
                <input
                  className="ar-input"
                  {...aria}
                  value={draft.title}
                  maxLength={300}
                  onChange={(event) => {
                    onEdit({ ...draft, title: event.currentTarget.value })
                  }}
                />
              )}
            </Field>
            <Field
              label="Meta description"
              help="The sentence under the headline in Google. Not a ranking factor; it is what decides whether anybody clicks."
            >
              {(aria) => (
                <textarea
                  className="ar-textarea"
                  {...aria}
                  value={draft.meta}
                  maxLength={500}
                  onChange={(event) => {
                    onEdit({ ...draft, meta: event.currentTarget.value })
                  }}
                />
              )}
            </Field>
            <MarkdownEditor
              label="Body"
              value={draft.body}
              busy={saving}
              error={saveFailure}
              onChange={(body) => {
                onEdit({ ...draft, body })
              }}
            />
          </>
        ) : null}

        {images.length === 0 ? null : (
          <div className="ar-row app-diff-head">
            {/* SEO-UI-90. The heading row S-D5 puts over the image block and this
                screen had not got: without it the picture and its alt-text field
                arrive between the body editor and the footer with nothing saying
                what they are or where they came from. */}
            <strong className="app-label-sm">Image — generated for this section</strong>
            <span className="ar-subtle app-caption">
              {images.length === 1 ? '1 per article' : `${String(images.length)} on this article`}
            </span>
          </div>
        )}

        {images.map((image, index) => (
          <div key={image.index} className="ar-grid ar-grid--2">
            {image.url === '' ? null : (
              <img className="app-approve__img" src={image.url} alt={draft.alts[index] ?? ''} />
            )}
            <div className="ar-stack ar-stack--tight">
              <Field
                label="Alt text"
                help="Written with the image, not bolted on later — it is what the technical audit checks for and what a screen reader gets."
              >
                {(aria) => (
                  <input
                    className="ar-input"
                    {...aria}
                    // **Falls through to the stored alt**, rather than reading the
                    // draft alone. `alts` is positional against a list an image
                    // press can shorten under it, so a held draft's index 0 was the
                    // *dropped* picture's sentence — which then described the
                    // surviving one, and would have been saved onto it. `onImageChanged`
                    // empties the array and this is what refills the fields from the
                    // server's answer.
                    value={draft.alts[index] ?? image.alt}
                    onChange={(event) => {
                      // Seeded from what is on screen, not from the draft, so
                      // editing one alt cannot leave a hole at an earlier index that
                      // the fall-through would then fill from a different picture.
                      const alts = images.map((each, position) => draft.alts[position] ?? each.alt)
                      alts[index] = event.currentTarget.value
                      onEdit({ ...draft, alts })
                    }}
                  />
                )}
              </Field>
              {contentId === null ? null : (
                <ImageActions
                  contentId={contentId}
                  index={image.index}
                  onChanged={onImageChanged}
                />
              )}
              <span className="ar-subtle app-caption">
                Generated images never show recognisable people or a competitor&apos;s work, and
                never claim to be a photograph of your project.
              </span>
            </div>
          </div>
        ))}

        {/*
          Two captions, and which one shows is the API's `publish_target`. With
          a target the approval itself queues the publish; without one it
          records the decision and the sentence says where to choose a target,
          because that — not a second press — is what stands between the tenant
          and a live page.
        */}
        {publishTarget && technical ? (
          <p className="ar-subtle app-caption" style={{ margin: 0 }}>
            Approving writes these fixes to {detail.site_domain ?? 'the site'} through{' '}
            {targetName(publishTarget)}: a title, a description or alt text is written from the
            page&apos;s own words, and anything {targetName(publishTarget)} has no field for comes
            back here with the exact text to paste. Nothing else on the page is touched.
          </p>
        ) : publishTarget ? (
          <p className="ar-subtle app-caption" style={{ margin: 0 }}>
            Approving records the decision against your name and publishes it to{' '}
            {targetName(publishTarget)} straight away, with the exact text you approved.{' '}
            <Link className="ar-link" to="/seo/publish-targets">
              Where we publish
            </Link>{' '}
            shows it land.
          </p>
        ) : (
          <p className="ar-subtle app-caption" style={{ margin: 0 }}>
            Approving records the decision against your name and marks this ready. Nothing is pushed
            until {detail.site_domain ?? 'this site'} has somewhere to publish — choose git,
            WordPress, Webflow or Shopify under{' '}
            <Link className="ar-link" to="/settings/publish-target">
              Settings, Publishing
            </Link>
            , and approvals after that go out on their own.
          </p>
        )}
      </div>

      <div className="ar-card__footer">
        <Button tone="primary" writes busy={deciding} busyLabel="Approving" onClick={onApprove}>
          {publishTarget ? (technical ? 'Approve & apply' : 'Approve & publish') : 'Approve'}
        </Button>
        {editable ? (
          <Button
            tone="ghost"
            writes
            disabled={!dirty}
            busy={saving}
            busyLabel="Saving"
            onClick={onSave}
          >
            Save my edits
          </Button>
        ) : null}
        <span className="ar-card__spacer" />
        <Button tone="quiet" writes onClick={onReject}>
          Reject
        </Button>
      </div>
    </div>
  )
}

/**
 * The three presses S-D5 puts under an article's picture — SEO-UI-64.
 *
 * *Regenerate*, *Upload my own*, *Publish without an image*, and the screen had
 * none of them: the alt-text field was the only thing a tenant could change about
 * an illustration, and `PUT …/edits` still accepts `image_alt` and nothing else.
 * That left the one element on the page that is not words with no answer except
 * rejecting the whole article.
 *
 * **Its own component, holding its own mutations**, rather than three more
 * `useMutation`s in `QueueTab`. They are the only calls on this screen addressed
 * to the *document* rather than to the approval, and per-image at that — one
 * article may carry more than one — so hoisting them would mean threading an
 * index through `Change` to reach a button that already knows it.
 *
 * **The upload is base64 in a JSON body**, matching `ImageUploadIn`, which argues
 * the choice from the API's end. `FileReader.readAsDataURL` gives
 * `data:<mime>;base64,<payload>` and the payload after the comma is exactly what
 * the field wants.
 */
function ImageActions({
  contentId,
  index,
  onChanged,
}: {
  contentId: string
  index: number
  onChanged: () => void
}): JSX.Element {
  const toast = useToast()
  const [dropping, setDropping] = useState(false)
  const [reading, setReading] = useState<string | null>(null)
  const file = useRef<HTMLInputElement>(null)

  const regenerate = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/content-images/{content_id}/{index}/regenerate', {
        params: { path: { content_id: contentId, index } },
      }),
    ),
  )

  const upload = useMutation((body: { data: string; mime_type: string }) =>
    call(() =>
      api.PUT('/api/v1/seo/content-images/{content_id}/{index}', {
        params: { path: { content_id: contentId, index } },
        body: { data: body.data, mime_type: body.mime_type, alt: null },
      }),
    ),
  )

  const drop = useMutation(() =>
    call(() =>
      api.DELETE('/api/v1/seo/content-images/{content_id}/{index}', {
        params: { path: { content_id: contentId, index } },
      }),
    ),
  )

  function chosen(picked: File): void {
    setReading(null)
    const reader = new FileReader()
    reader.onerror = () => {
      // A file the browser could not open — a network drive that went away
      // between the picker and the read. Said here rather than as a failed
      // request, because no request was made.
      setReading('That file could not be read from your computer.')
    }
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const payload = result.slice(result.indexOf(',') + 1)
      if (payload === '') {
        setReading('That file is empty.')
        return
      }
      void upload.run({ data: payload, mime_type: picked.type || 'image/jpeg' }).then((done) => {
        if (!done.ok) return
        onChanged()
        toast.show({
          kind: 'success',
          title: 'Your picture is on the draft',
          body: 'Check the alt text still describes it — it is what the audit reads and what a screen reader gets.',
        })
      })
    }
    reader.readAsDataURL(picked)
  }

  return (
    <>
      <InlineError
        failure={regenerate.state.failure ?? upload.state.failure ?? drop.state.failure}
      />
      {reading === null ? null : (
        <p className="app-pub__error" style={{ margin: 0 }}>
          {reading}
        </p>
      )}
      <div className="ar-row">
        <Button
          size="sm"
          writes
          busy={regenerate.pending}
          busyLabel="Drawing"
          onClick={() => {
            void regenerate.run(undefined).then((result) => {
              if (!result.ok) return
              onChanged()
              toast.show({
                kind: 'success',
                title: 'Drawing another one',
                body:
                  result.data.note ||
                  'It replaces this picture when it arrives. Drawing one is a charge, so it happens once per press.',
              })
            })
          }}
        >
          Regenerate
        </Button>
        <Button
          size="sm"
          writes
          busy={upload.pending}
          busyLabel="Uploading"
          onClick={() => {
            file.current?.click()
          }}
        >
          Upload my own
        </Button>
        <Button
          tone="quiet"
          size="sm"
          writes
          onClick={() => {
            setDropping(true)
          }}
        >
          Publish without an image
        </Button>
      </div>
      {/* `accept` is a hint to the picker and nothing more — the API sniffs the
          magic bytes and refuses anything that is not actually one of the three.
          It is here so a tenant is not offered their whole disk. */}
      <input
        ref={file}
        type="file"
        className="ar-visually-hidden"
        accept="image/jpeg,image/png,image/webp"
        aria-label="Choose a picture for this article"
        onChange={(event) => {
          const picked = event.currentTarget.files?.[0]
          // Cleared so choosing the same file twice fires again — a tenant who
          // re-exported over the same filename means it the second time.
          event.currentTarget.value = ''
          if (picked !== undefined) chosen(picked)
        }}
      />

      {/* Dropping the picture is the one press here that takes something away, and
          the article can still be approved without it — so it asks, on the same
          argument the pipeline's *Discard* does. The other two replace. */}
      {dropping ? (
        <Modal
          title="Publish without a picture?"
          onClose={() => {
            setDropping(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setDropping(false)
                }}
              >
                Keep the picture
              </Button>
              <Button
                tone="danger"
                writes
                busy={drop.pending}
                busyLabel="Removing"
                onClick={() => {
                  void drop.run(undefined).then((result) => {
                    if (!result.ok) return
                    setDropping(false)
                    onChanged()
                    toast.show({
                      kind: 'success',
                      title: 'It will publish without a picture',
                      body: 'The image comes off the draft. It is not deleted, so Regenerate can still put one back.',
                    })
                  })
                }}
              >
                Publish without it
              </Button>
            </>
          }
        >
          <InlineError failure={drop.state.failure} />
          <p style={{ marginTop: 0 }}>
            The article publishes with no illustration. Nothing is deleted — the picture stays in
            your account, and <em>Regenerate</em> draws a fresh one if you change your mind.
          </p>
        </Modal>
      ) : null}
    </>
  )
}

function FieldDiff({ field }: { field: ApprovalField }): JSX.Element {
  return (
    <div>
      <div className="ar-row app-diff-head">
        <strong className="app-label-sm">{field.label}</strong>
        <span className="ar-subtle app-caption">
          {`${String(field.before_chars)} → ${String(field.after_chars)} characters`}
        </span>
      </div>
      <div className="app-diff">
        {field.before === '' ? null : <DiffRow line={{ op: 'del', text: field.before }} />}
        <DiffRow line={{ op: field.changed ? 'add' : 'ctx', text: field.after }} />
      </div>
    </div>
  )
}

/** One line of the diff. The gutter carries the sign, so it is never colour alone. */
function DiffRow({ line }: { line: ApprovalLine }): JSX.Element {
  const modifier =
    line.op === 'add' ? ' app-diff__row--add' : line.op === 'del' ? ' app-diff__row--del' : ''
  return (
    <div className={`app-diff__row${modifier}`}>
      <span className="app-diff__gut">
        {line.op === 'add' ? '+' : line.op === 'del' ? '−' : ' '}
      </span>
      <span className="app-diff__txt">{line.text}</span>
    </div>
  )
}

/* --- why ---------------------------------------------------------------------- */

/**
 * The reasoning, quoted rather than restated.
 *
 * Every line here is a `reasoning_logs` row as it was written, with the model
 * that wrote it — `ApprovalRationaleOut` says so, and the point is that the
 * sentence a tenant reads before approving is the sentence the run recorded, not
 * a summary this screen composed of it.
 */
function Why({
  detail,
  article,
}: {
  detail: ApprovalDetail | null
  article: ContentDetail | null
}): JSX.Element {
  const brand = useBrand()
  const steps: Rationale[] = detail?.diff.why ?? []
  const model = [...steps].reverse().find((step) => step.model !== null && step.model !== undefined)
  const tokensIn = steps.reduce((total, step) => total + (step.input_tokens ?? 0), 0)
  const tokensOut = steps.reduce((total, step) => total + (step.output_tokens ?? 0), 0)

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Why we suggest this</h2>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight app-why">
        {steps.length === 0 ? (
          <p className="ar-subtle" style={{ margin: 0 }}>
            No reasoning was recorded for this change. That is worth knowing before you approve it —
            everything {brand.productName} writes should be able to say why.
          </p>
        ) : (
          steps.map((step) => (
            <p key={step.step_number} style={{ margin: 0 }}>
              <strong>{phaseLabel(step.phase)}.</strong> {step.summary}
            </p>
          ))
        )}
        {detail === null ? null : (
          <>
            <hr className="app-approve__rule" />
            <div className="ar-row app-diff-head">
              <span className="ar-subtle">Model</span>
              <span className="ar-mono">{model?.model ?? 'Not recorded'}</span>
            </div>
            <div className="ar-row app-diff-head">
              <span className="ar-subtle">Drafted</span>
              <span className="ar-mono">{formatDate(detail.created_at)}</span>
            </div>
            <div className="ar-row app-diff-head">
              <span className="ar-subtle">Words</span>
              <span className="ar-mono">{String(detail.word_count)}</span>
            </div>
            {/* **Tokens where S-D5 draws ₹4.20**, and it is the same call
                `ReasoningTrail` made about the prototype's per-run rupee badge:
                nothing joins a document to money. `usage_metering` is keyed by
                tenant, period and metric, and most of a pass's stages run
                in-process without a `job_id`, so any figure here would be
                composed by this screen rather than derived from the ledger —
                and a number that reconciles against nothing is worse than no
                number. The tokens and the model do reconcile, and the money
                stays on the budget-burn panel where `app/v1/spend.py` derives
                it from the ledger that holds it. */}
            {tokensIn + tokensOut === 0 ? null : (
              <div className="ar-row app-diff-head">
                <span className="ar-subtle">Tokens</span>
                <span className="ar-mono">
                  {`${tokensIn.toLocaleString('en-IN')} in · ${tokensOut.toLocaleString('en-IN')} out`}
                </span>
              </div>
            )}
            {/* §V2's *"See the full reasoning trail →"*. It carries the run so
                the viewer opens on **this** article's chain rather than on the
                newest one — a link that lands on somebody else's reasoning is
                worse than no link. Absent when the document records no run,
                which is true of anything written before S-B29 persisted them. */}
            {article?.run_id ? (
              <Link className="ar-link" to={`/seo/trail?run=${article.run_id}`}>
                See the full reasoning trail →
              </Link>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/* --- the quality gate --------------------------------------------------------- */

/** `QualityCheckOut.code` → the mockup's label, in the order it lists them. */
const GATE_CHECKS: Record<string, string> = {
  originality: 'Originality',
  fact_check: 'Fact check',
  brand_voice: 'Brand voice',
  eeat: 'E-E-A-T',
  internal_links: 'Internal links',
}

const GATE_VERDICT: Record<string, { tone: string; mark: string }> = {
  pass: { tone: 'ar-badge--success', mark: '✓' },
  warn: { tone: 'ar-badge--warning', mark: '⚠' },
  fail: { tone: 'ar-badge--danger', mark: '✕' },
}

/**
 * What the quality gate made of this draft — audit §G13, and the missing card
 * §V2 records in the rail.
 *
 * **Everything in the queue has passed**, which is the point rather than a
 * reason to leave it out: `quality_failed` is a terminal state and a blocked
 * draft never reaches an approver at all. So this card is the evidence for a
 * decision somebody is about to make with their name on it — *four claims
 * sourced, 3% similarity, brand voice matched* — and without it the approver was
 * being asked to trust that a gate they could not see had run.
 *
 * A verdict older than S-B26 has no stored gate, and that says so rather than
 * showing five empty rows: `_gate_out` returns null for those documents by
 * design, and a screen inventing ticks for checks that never ran would be the
 * worse failure.
 */
function QualityGate({
  article,
  failure,
}: {
  article: ContentDetail | null
  failure: ApiFailure | null
}): JSX.Element {
  const checks: QualityCheck[] = article?.gate?.checks ?? []
  const order = Object.keys(GATE_CHECKS)
  const rows = [
    ...order
      .map((code) => checks.find((check) => check.code === code))
      .filter((check): check is QualityCheck => check !== undefined),
    ...checks.filter((check) => !order.includes(check.code)),
  ]

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h2>Quality gate</h2>
        {article === null || rows.length === 0 ? null : (
          <>
            <span className="ar-card__spacer" />
            <span
              className={`ar-badge ${article.gate?.blocked === true ? 'ar-badge--danger' : 'ar-badge--success'}`}
            >
              {article.gate?.blocked === true ? '✕ Blocked' : '✓ Passed'}
            </span>
          </>
        )}
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {/* SEO-UI-58's other half, and without it the override produces a screen
            nobody can read: a card in the approval queue whose gate says
            *"✕ Blocked"*, with nothing saying how it got here or who decided it
            should. Everything else in this queue passed — `quality_failed` is
            terminal and a blocked draft never reaches an approver — so a blocked
            verdict on this card means exactly one thing, and it has to say so
            **before** the objections rather than after, because the objections are
            what the reader is about to weigh. */}
        {article?.overridden === true ? (
          <div className="ar-alert ar-alert--warning">
            <span className="ar-alert__icon" aria-hidden="true">
              ⚠
            </span>
            <div className="ar-alert__body">
              <strong>The gate blocked this, and somebody asked to see it anyway</strong>
              Nothing was re-judged and no limit was changed — the objections below are the ones the
              gate raised. You are the check now.
            </div>
          </div>
        ) : null}
        {failure !== null ? (
          <p className="ar-subtle" style={{ margin: 0 }}>
            The quality gate could not be loaded. {failure.message}
          </p>
        ) : article === null ? (
          <Loading label="Loading the quality gate" lines={2} />
        ) : rows.length === 0 ? (
          <p className="ar-subtle" style={{ margin: 0 }}>
            No gate result was recorded for this draft. Anything written before the quality gate
            existed has none, and nothing is invented for it here.
          </p>
        ) : (
          rows.map((check) => {
            const verdict = GATE_VERDICT[check.verdict] ?? { tone: 'ar-badge--neutral', mark: '·' }
            return (
              <div className="app-gate__row" key={check.code}>
                <span className={`ar-badge ${verdict.tone}`}>{verdict.mark}</span>
                <span className="app-gate__grow">
                  <strong>{GATE_CHECKS[check.code] ?? check.code}</strong>
                  {check.reason === '' ? null : <span>{check.reason}</span>}
                </span>
                {check.headline === '' ? null : <span className="ar-mono">{check.headline}</span>}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

/* --- the list ----------------------------------------------------------------- */

/**
 * The change types S-D5's *"Approve all safe changes"* means — SEO-UI-62.
 *
 * The two the gating screen itself calls low risk, in its own words: internal
 * links *"do not change your words"*, and technical fixes are *"invisible to
 * visitors"*. The other two are the ones whose blast-radius sentences say a page
 * appears on your site and that this is visible to the public immediately.
 *
 * The codes are `app/seo/gating.CHANGES`, and this list is deliberately a subset
 * of it rather than a rule about it: *"low risk"* is a judgement somebody made
 * about four named things, not a property a fifth change type would inherit by
 * being added.
 */
const LOW_RISK: readonly string[] = ['internal_links', 'technical']

function WaitingList({
  items,
  selected,
  checked,
  busy,
  nextCursor,
  canPageBack,
  onSelect,
  onCheck,
  onCheckLowRisk,
  onApproveSelected,
  onOlder,
  onNewer,
}: {
  items: Approval[]
  selected: string | null
  checked: readonly string[]
  busy: boolean
  nextCursor: string | null
  canPageBack: boolean
  onSelect: (id: string) => void
  onCheck: (id: string, on: boolean) => void
  onCheckLowRisk: (ids: readonly string[]) => void
  onApproveSelected: () => void
  onOlder: (cursor: string) => void
  onNewer: () => void
}): JSX.Element {
  const lowRisk = items.filter((item) => LOW_RISK.includes(item.change_type)).map((item) => item.id)

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        {/* SEO-UI-92. *Also waiting*, which is S-D5's, and it is the more accurate
            of the two once the list includes the open item: *Waiting* reads as
            everything outstanding, and this list is *the rest of it, plus where
            you are*. The open item stays in — unlike the drawing, where the list
            is passive — because here it is the navigation control for the queue,
            and a picker that hides your current position is one you cannot use to
            get back to it. `aria-current` on the row is what says which. */}
        <h2>Also waiting</h2>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--neutral">{String(items.length)}</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        <ul className="ar-stack ar-stack--tight app-approve__list">
          {items.map((item) => (
            <li key={item.id} className="ar-panel app-approve__row">
              <label className="ar-choice ar-choice--check">
                <input
                  type="checkbox"
                  checked={checked.includes(item.id)}
                  aria-label={`Select ${item.title ?? item.change_label}`}
                  onChange={(event) => {
                    onCheck(item.id, event.currentTarget.checked)
                  }}
                />
                <span className="ar-choice__box" aria-hidden="true" />
              </label>
              <button
                type="button"
                className="app-approve__pick"
                aria-current={item.id === selected ? 'true' : undefined}
                onClick={() => {
                  onSelect(item.id)
                }}
              >
                <strong className="app-label-sm">{item.title ?? item.change_label}</strong>
                <span className="ar-subtle app-editor__block">
                  {[
                    item.change_label,
                    item.site_domain ?? 'This organisation',
                    formatDate(item.created_at),
                  ].join(' · ')}
                </span>
                {item.stale ? (
                  <span className="ar-badge ar-badge--warning">Waiting a while</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
        {nextCursor === null && !canPageBack ? null : (
          <div className="ar-btn-group">
            <Button size="sm" disabled={!canPageBack} onClick={onNewer}>
              Newer
            </Button>
            <Button
              size="sm"
              disabled={nextCursor === null}
              onClick={() => {
                if (nextCursor !== null) onOlder(nextCursor)
              }}
            >
              Older
            </Button>
          </div>
        )}
      </div>
      {/* SEO-UI-62. S-D5 draws *"Approve all safe changes"* in the page header, as
          one press. **This selects rather than approves, and it is here rather
          than up there**, and both halves are deliberate.

          *Selects*, because a single press that approves a category without
          anybody reading it is a thing this product already has, done properly:
          the gating tab beside this one turns exactly those change types
          automatic, permanently, with the blast radius written next to each. A
          second, per-batch spelling of it in the queue would be the same decision
          made worse — same effect, no record of what was agreed to, and it would
          have to be re-made every morning. Ticking the low-risk ones gives the
          one-press ergonomics the row asks for and leaves *Approve selected*
          where it is: an approval somebody pressed, with what they approved on
          screen in front of them.

          *Here*, because a header button eight hundred pixels from the list would
          silently change checkbox state the presser cannot see. The control that
          ticks the boxes belongs on the card that holds them. */}
      <div className="ar-card__footer">
        <Button
          tone="primary"
          size="sm"
          disabled={checked.length === 0}
          busy={busy}
          busyLabel="Approving"
          onClick={onApproveSelected}
        >
          Approve selected
        </Button>
        {lowRisk.length === 0 ? null : (
          <Button
            size="sm"
            onClick={() => {
              onCheckLowRisk(lowRisk)
            }}
          >
            {`Tick the ${String(lowRisk.length)} low-risk`}
          </Button>
        )}
        <span className="ar-subtle app-caption">
          {checked.length === 0
            ? 'Tick the ones you have read.'
            : `${String(checked.length)} chosen`}
        </span>
      </div>
    </div>
  )
}

/* --- plumbing ------------------------------------------------------------------ */

interface Draft {
  id: string
  title: string
  meta: string
  body: string
  alts: string[]
}

function draftOf(detail: ApprovalDetail): Draft {
  return {
    id: detail.id,
    title: detail.title ?? '',
    meta: detail.meta_description,
    body: detail.body_markdown ?? '',
    alts: (detail.images ?? []).map((image) => image.alt),
  }
}

function sameDraft(a: Draft, b: Draft): boolean {
  return (
    a.title === b.title &&
    a.meta === b.meta &&
    a.body === b.body &&
    // **Compared through the fall-through the field renders with.** `b.alts` is
    // emptied whenever an image press changes the list (see `onImageChanged`), and
    // a length comparison would then call every draft dirty for ever — an *Unsaved
    // edits* badge on a document with nothing unsaved, and *Save my edits* enabled
    // to send the same text back. An index the draft does not carry is the stored
    // one, which is exactly what the input shows.
    a.alts.every((alt, index) => alt === (b.alts[index] ?? alt))
  )
}

/** `reasoning_logs.phase`, in the tenant's language. Unknown phases pass through. */
function phaseLabel(phase: string): string {
  return PHASES[phase] ?? phase
}

const PHASES: Record<string, string> = {
  audit: 'What we looked at',
  decision: 'What we decided',
  action: 'What we did',
  research: 'What we found',
  outline: 'How we planned it',
  draft: 'What we wrote',
  review: 'What we checked',
}

/**
 * The target's name as the settings screens print it. Restated rather than
 * imported from `Sites.tsx`, which keeps its own map private; anything
 * unrecognised falls through to the raw key rather than to "Unknown".
 */
function targetName(target: string): string {
  const names: Record<string, string> = {
    git: 'your git repository',
    wordpress: 'WordPress',
    webflow: 'Webflow',
    shopify: 'Shopify',
    download: 'a file to download',
  }
  return names[target] ?? target
}

type FixResultsOut = components['schemas']['FixResultsOut']

/**
 * What applying a technical fix did, page by page (`app/seo/fixapply.py`).
 *
 * Two lists per page and both are shown, because the second is the honest
 * half: a target with no field for a meta description hands the text back,
 * and a card that showed only the green ticks would claim a fix that never
 * reached the page. The value is rendered in a `pre` so it can be selected
 * and pasted as written — a JSON-LD block or a sitemap runs to many lines.
 */
function FixResults({ results }: { results: FixResultsOut }): JSX.Element {
  const pages = results.pages ?? []
  const when = results.at ? new Date(results.at).toLocaleString() : null
  return (
    <section aria-label="What was applied" className="ar-stack ar-stack--tight">
      <strong className="app-label-sm">What happened</strong>
      <p className="ar-subtle app-caption" style={{ margin: 0 }}>
        {results.error
          ? results.error
          : `${String(results.applied)} ${results.applied === 1 ? 'change' : 'changes'} applied` +
            (results.target ? ` through ${targetName(results.target)}` : '') +
            (results.left > 0 ? `, ${String(results.left)} handed back for you to paste` : '') +
            (results.failed > 0
              ? `, ${String(results.failed)} ${results.failed === 1 ? 'page' : 'pages'} could not be reached`
              : '') +
            (when ? ` — ${when}` : '') +
            '.'}
      </p>
      {pages.map((page) => (
        <div key={page.url} className="ar-panel ar-stack ar-stack--tight">
          <span className="ar-mono ar-subtle app-editor__block">{page.url}</span>
          {page.error ? (
            <span className="ar-subtle" role="status">
              Not changed: {page.error}
            </span>
          ) : null}
          {(page.applied ?? []).map((item) => (
            <div key={`applied-${item.kind}-${item.detail}`}>
              <strong className="app-label-sm">✓ {fixKindLabel(item.kind)}</strong>
              <span className="ar-subtle app-editor__block">{item.detail}</span>
            </div>
          ))}
          {(page.left ?? []).map((item, index) => (
            <div key={`left-${item.kind}-${String(index)}`}>
              <strong className="app-label-sm">For you: {fixKindLabel(item.kind)}</strong>
              <span className="ar-subtle app-editor__block">{item.reason}</span>
              {item.value === '' ? null : (
                <pre className="ar-mono app-editor__block" style={{ whiteSpace: 'pre-wrap' }}>
                  {item.value}
                </pre>
              )}
            </div>
          ))}
        </div>
      ))}
    </section>
  )
}

/** `FIX_KINDS`, in the tenant's words. Unrecognised falls through to the key. */
function fixKindLabel(kind: string): string {
  const labels: Record<string, string> = {
    title: 'Page title',
    meta_description: 'Meta description',
    h1: 'Heading',
    alt_text: 'Image alt text',
    structured_data: 'Structured data',
    sitemap: 'Sitemap',
  }
  return labels[kind] ?? kind
}
